'use client';

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@cobrastyle/ui';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * `id` of the heading inside `children` that names this dialog. Required
   * rather than a `title` prop: the paket quick view styles its own heading as
   * part of the layout, so the dialog is labelled by that element instead of a
   * chrome title bar it does not have.
   */
  labelledBy: string;
  children: ReactNode;
  className?: string;
}

/** Elements that can hold focus, for the tab cycle below. */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * A centered modal dialog.
 *
 * `@cobrastyle/ui` ships only `Drawer`, which is anchored to an edge and always
 * draws its own title bar — wrong for a quick view that is a two-column panel
 * with its own heading. This borrows Drawer's portal, scroll-lock and Escape
 * handling and adds what a dialog needs and Drawer lacks: `role="dialog"`,
 * `aria-modal`, a focus trap, and focus restored to the trigger on close.
 *
 * Kept local to the app rather than pushed upstream: one consumer today, and an
 * upstream change would need a `@cobrastyle/ui` release to reach this repo.
 */
export function Modal({ isOpen, onClose, labelledBy, children, className }: ModalProps) {
  const [mounted, setMounted] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  // Captured on open, restored on close — see the focus-return effect.
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Lock body scroll while open, reserving the scrollbar's width as padding so
  // hiding it does not reflow the page wider. Previous inline values are put
  // back, which is what lets a close restore a page that was already locked.
  useEffect(() => {
    if (!isOpen) return;

    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    const prevOverflow = document.body.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;

    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPaddingRight;
    };
  }, [isOpen]);

  // Move focus in on open and hand it back on close. Without the hand-back,
  // closing a quick view opened from the fifth thumbnail drops the keyboard at
  // the top of the document, and the visitor has to tab back down.
  // `mounted` is a dependency, not just a guard: the first render returns null
  // so the portal — and with it `panelRef` — does not exist yet. Without it the
  // effect runs once against a null panel and never fires again, because
  // `isOpen` has not changed by the time the panel is in the DOM.
  useEffect(() => {
    if (!isOpen || !mounted) return;

    previouslyFocused.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const panel = panelRef.current;
    const first = panel?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? panel)?.focus();

    return () => previouslyFocused.current?.focus();
  }, [isOpen, mounted]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }

      if (e.key !== 'Tab') return;

      // Trap: a dialog the keyboard can walk out of leaves the visitor tabbing
      // through the page behind an opaque overlay.
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)];
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleKeyDown]);

  if (!mounted || !isOpen) return null;

  return createPortal(
    <div
      data-testid="modal-backdrop"
      onClick={onClose}
      className="fixed inset-0 z-[100] flex items-end justify-center bg-foreground/70 p-0 backdrop-blur-[3px] sm:items-center sm:p-10"
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        // The panel is inside the backdrop, so a click on the product name would
        // bubble up and dismiss the product being read.
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'relative flex max-h-[92vh] w-full flex-col overflow-y-auto overscroll-contain rounded-t-lg border border-accent-600/35 bg-card shadow-2xl outline-none',
          'sm:max-h-[85vh] sm:max-w-[900px] sm:rounded-md',
          className,
        )}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
